import React from 'react';
import { Box, Link, Stack, Typography } from '@mui/material';
import FormattedText from './FormattedText';
import MessageActionButtons from './MessageActionButtons';
import MessageHeaderBlock from './MessageHeaderBlock';
import useMessageStyles from '../../../hooks/useMessageStyles';
import useInterveneTheme from '../../../hooks/useInterveneTheme';

export default function CardMessage({ message }) {
  const {
    messageFooterSx,
    messageTypeLabelSx,
    messageRichStackSx,
    messageOptionCardSx,
    messageEmphasisTextSx,
    messageSecondaryTextSx,
    messageBodyTextSx,
  } = useMessageStyles();
  const { chat } = useInterveneTheme();
  const card = message.card;
  if (!card) return null;

  const linkButtons = (card.linkButtons || []).map((button) => ({
    ...button,
    label: button.label,
  }));

  const readOnlyButtons = (card.readOnlyButtons || []).map((label) => ({
    label,
    disabled: true,
  }));

  const handleLinkClick = (button) => {
    if (button.url) {
      window.open(button.url, '_blank', 'noopener,noreferrer');
    }
  };

  const hasStructuredHeader = Boolean(message.header?.url || message.header?.text);

  return (
    <Stack spacing={0.75} sx={messageRichStackSx}>
      {card.badge && (
        <Typography component="span" sx={messageTypeLabelSx}>
          {card.badge}
        </Typography>
      )}

      {hasStructuredHeader ? (
        <MessageHeaderBlock header={message.header} bleed />
      ) : (
        card.header && (
          <Typography component="div" sx={messageEmphasisTextSx}>
            {card.header}
          </Typography>
        )
      )}

      {card.body && <FormattedText text={card.body} />}

      {card.items?.length > 0 && (
        <Box sx={{ ...messageOptionCardSx, mt: 0.25 }}>
          {card.items.map((item, index) => (
            <Box
              key={`${item.primary}-${index}`}
              sx={{
                py: 0.75,
                borderBottom:
                  index < card.items.length - 1 ? `1px solid ${chat.divider}` : 'none',
              }}
            >
              <Typography component="div" sx={messageEmphasisTextSx}>
                {item.primary}
              </Typography>
              {item.secondary && (
                <Typography component="div" sx={messageSecondaryTextSx}>
                  {item.secondary}
                </Typography>
              )}
            </Box>
          ))}
        </Box>
      )}

      {card.fields?.length > 0 && (
        <Stack spacing={0.5}>
          {card.fields.map((field) => (
            <Typography key={field.label} component="div" sx={messageBodyTextSx}>
              <Box component="span" sx={{ fontWeight: 600 }}>
                {field.label}:
              </Box>{' '}
              {field.value}
            </Typography>
          ))}
        </Stack>
      )}

      {card.productId && (
        <Typography component="div" sx={messageSecondaryTextSx}>
          Product ID: {card.productId}
        </Typography>
      )}

      {card.location?.url && (
        <Link
          href={card.location.url}
          target="_blank"
          rel="noopener noreferrer"
          underline="hover"
          sx={{ color: 'inherit', fontWeight: 500 }}
        >
          View on map
        </Link>
      )}

      {card.footer && (
        <Typography variant="caption" sx={messageFooterSx}>
          {card.footer}
        </Typography>
      )}

      <MessageActionButtons buttons={linkButtons} onButtonClick={handleLinkClick} />
      <MessageActionButtons buttons={readOnlyButtons} disabled />
    </Stack>
  );
}
